import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { Link } from 'react-router-dom';
import { db } from '../../config/firebase';
import { collection, query, where, getDocs, orderBy, Timestamp, onSnapshot } from 'firebase/firestore';
import { projectService } from '../../services/project.service';
import { Users, TrendingUp, CheckCircle, Target, Calendar, Clock, User, ChevronRight } from 'lucide-react';
import { DashboardTaskWidget } from '../../components/tasks/DashboardTaskWidget';
import { format, isAfter, startOfDay } from 'date-fns';
import { getLead } from '../../services/lead.service';
import { Lead } from '../../types/lead.types';
import { appointmentService } from '../../services/appointment.service';
import LeadDetailModal from '../../components/leads/LeadDetailModal';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { formatTimeRange } from '../../utils/dateUtils';

interface ProjectMetrics {
  projectId: string;
  projectName: string;
  totalStudents: number;
  enrolledStudents: number;
  validStudents: number;
  conversionRate: number;
  totalLeads: number;
}

interface DashboardAppointment {
  id: string;
  leadId: string;
  leadName?: string;
  projectId: string;
  userId: string;
  date: string;
  startTime: string;
  endTime: string;
  eventType: 'tour' | 'phone' | 'meeting';
  status: 'scheduled' | 'completed' | 'cancelled' | 'no-show';
  notes?: string;
}

const DashboardPage: React.FC = () => {
  const { userProfile } = useAuth();
  const { selectedProjectId } = useProject();
  const [projectMetrics, setProjectMetrics] = useState<ProjectMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [overallMetrics, setOverallMetrics] = useState({
    totalStudents: 0,
    enrolledStudents: 0,
    validStudents: 0,
    conversionRate: 0,
    totalLeads: 0,
    activeProjects: 0
  });

  // Appointments state
  const [appointments, setAppointments] = useState<DashboardAppointment[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [projectTimezones, setProjectTimezones] = useState<Record<string, string>>({});

  useEffect(() => {
    if (userProfile) {
      loadDashboardMetrics();
      loadUpcomingAppointments();
    }
    // Re-scope when the nav project selector changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, selectedProjectId]);

  // Helper to convert date field to string format (handles Timestamp, Date, or string)
  const getDateString = (dateField: any): string => {
    if (!dateField) return '';
    if (typeof dateField === 'string') return dateField;
    if (dateField.toDate && typeof dateField.toDate === 'function') {
      return format(dateField.toDate(), 'yyyy-MM-dd');
    }
    if (dateField instanceof Date) {
      return format(dateField, 'yyyy-MM-dd');
    }
    return '';
  };

  // Load upcoming appointments for the user's projects
  const loadUpcomingAppointments = async () => {
    if (!userProfile) return;

    setLoadingAppointments(true);
    try {
      // Get user's projects. When a project is selected in the nav, scope to it.
      const userProjects = await projectService.getUserProjects(userProfile.uid);
      const projectIds = (selectedProjectId
        ? userProjects.filter(p => p.id === selectedProjectId)
        : userProjects
      ).map(p => p.id);

      // Build project timezone lookup
      const tzMap: Record<string, string> = {};
      userProjects.forEach(p => {
        if (p.settings?.timezone) tzMap[p.id] = p.settings.timezone;
      });
      setProjectTimezones(tzMap);

      if (projectIds.length === 0) {
        setAppointments([]);
        setLoadingAppointments(false);
        return;
      }

      // Query appointments for user's projects, only scheduled status, ordered by date
      const today = format(startOfDay(new Date()), 'yyyy-MM-dd');

      // Firestore 'in' queries are limited to 10 items, so we may need to batch
      const batchSize = 10;
      const allAppointments: DashboardAppointment[] = [];

      for (let i = 0; i < projectIds.length; i += batchSize) {
        const batchProjectIds = projectIds.slice(i, i + batchSize);

        // Try without orderBy first to avoid index issues
        const appointmentsQuery = query(
          collection(db, 'appointments'),
          where('projectId', 'in', batchProjectIds),
          where('status', '==', 'scheduled')
        );

        const snapshot = await getDocs(appointmentsQuery);

        snapshot.docs.forEach(doc => {
          const data = doc.data();
          // Convert date to string format (handles Timestamp, Date, or string)
          const dateStr = getDateString(data.date);
          // Only include future appointments
          if (dateStr && dateStr >= today) {
            allAppointments.push({
              id: doc.id,
              leadId: data.leadId,
              leadName: data.leadName || data.parentName,
              projectId: data.projectId,
              userId: data.userId,
              date: dateStr,
              startTime: data.startTime,
              endTime: data.endTime,
              eventType: data.eventType || 'meeting',
              status: data.status,
              notes: data.notes
            });
          }
        });
      }

      // Sort by date and time, limit to 5
      allAppointments.sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.startTime.localeCompare(b.startTime);
      });

      setAppointments(allAppointments.slice(0, 5));
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const handleViewAppointmentLead = async (appointment: DashboardAppointment) => {
    try {
      const lead = await getLead(appointment.leadId);
      if (lead) {
        setSelectedLead(lead);
        setShowLeadModal(true);
      }
    } catch (error) {
      console.error('Error fetching lead:', error);
    }
  };

  const handleAppointmentStatusUpdate = async (appointmentId: string, newStatus: 'completed' | 'cancelled' | 'no-show') => {
    try {
      await appointmentService.updateAppointment(appointmentId, { status: newStatus });
      // Refresh appointments
      loadUpcomingAppointments();
    } catch (error) {
      console.error('Error updating appointment:', error);
    }
  };

  const loadDashboardMetrics = async () => {
    if (!userProfile) return;
    
    try {
      setLoading(true);
      
      // Get all projects for the user. When a project is selected in the nav,
      // scope the dashboard metrics to just that project.
      const userProjects = await projectService.getUserProjects(userProfile.uid);
      const activeProjects = userProjects
        .filter(p => p.status === 'active')
        .filter(p => !selectedProjectId || p.id === selectedProjectId);
      
      const projectMetricsData: ProjectMetrics[] = [];
      let totalOverallStudents = 0;
      let totalOverallEnrolled = 0;
      let totalOverallValid = 0;
      let totalOverallLeads = 0;
      
      // Calculate metrics for each project
      for (const project of activeProjects) {
        const leadsQuery = query(
          collection(db, 'leads'),
          where('projectId', '==', project.id)
        );
        const snapshot = await getDocs(leadsQuery);
        const projectLeads = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        } as Lead));

        let projectStudents = 0;
        let projectEnrolled = 0;
        let projectValid = 0;
        
        projectLeads.forEach(lead => {
          const children = lead.children || [];
          projectStudents += children.length;
          
          if (lead.status === 'enrolled') {
            projectEnrolled += children.length;
          }
          
          if (lead.determination === 'valid') {
            projectValid += children.length;
          }
        });
        
        const projectConversionRate = projectValid > 0 
          ? Math.round((projectEnrolled / projectValid) * 100) 
          : 0;

        projectMetricsData.push({
          projectId: project.id,
          projectName: project.name,
          totalStudents: projectStudents,
          enrolledStudents: projectEnrolled,
          validStudents: projectValid,
          conversionRate: projectConversionRate,
          totalLeads: projectLeads.length
        });

        totalOverallStudents += projectStudents;
        totalOverallEnrolled += projectEnrolled;
        totalOverallValid += projectValid;
        totalOverallLeads += projectLeads.length;
      }
      
      const overallConversionRate = totalOverallValid > 0 
        ? Math.round((totalOverallEnrolled / totalOverallValid) * 100) 
        : 0;

      setProjectMetrics(projectMetricsData);
      setOverallMetrics({
        totalStudents: totalOverallStudents,
        enrolledStudents: totalOverallEnrolled,
        validStudents: totalOverallValid,
        conversionRate: overallConversionRate,
        totalLeads: totalOverallLeads,
        activeProjects: activeProjects.length
      });
    } catch (error) {
      console.error('Error loading dashboard metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, icon, gradient }: any) => (
    <div className={`overflow-hidden shadow-lg rounded-xl ${gradient} text-white`}>
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="p-3 rounded-lg bg-white/20 backdrop-blur-sm">
              {icon}
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-white/80 truncate">{title}</dt>
              <dd className="text-2xl font-bold">{value}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="mt-2 text-gray-600">
                Welcome back, {userProfile?.name || 'User'}!
              </p>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
              <div className="mb-8">
                <DashboardTaskWidget />
              </div>

              <div className="bg-white overflow-hidden shadow-lg rounded-xl mb-8 border border-gray-100">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Quick Actions
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Field role quick actions */}
                    {userProfile?.role === 'field' && (
                      <>
                        <Link
                          to="/leads/new"
                          className="group relative rounded-xl border-2 border-accent-100 bg-gradient-to-br from-accent-50 to-white px-6 py-5 flex items-center space-x-3 hover:border-accent-300 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="absolute inset-0" aria-hidden="true" />
                            <p className="text-sm font-semibold text-accent-700 group-hover:text-accent-800">Add Lead</p>
                            <p className="text-sm text-gray-500 truncate">Capture a new lead</p>
                          </div>
                        </Link>
                        <Link
                          to="/canvass"
                          className="group relative rounded-xl border-2 border-primary-100 bg-gradient-to-br from-primary-50 to-white px-6 py-5 flex items-center space-x-3 hover:border-primary-300 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="absolute inset-0" aria-hidden="true" />
                            <p className="text-sm font-semibold text-primary-700 group-hover:text-primary-800">Start Canvassing</p>
                            <p className="text-sm text-gray-500 truncate">Open field canvass mode</p>
                          </div>
                        </Link>
                        <Link
                          to="/seek-find"
                          className="group relative rounded-xl border-2 border-coral-100 bg-gradient-to-br from-coral-50 to-white px-6 py-5 flex items-center space-x-3 hover:border-coral-300 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="absolute inset-0" aria-hidden="true" />
                            <p className="text-sm font-semibold text-coral-600 group-hover:text-coral-700">Seek &amp; Find</p>
                            <p className="text-sm text-gray-500 truncate">Tap-the-map outreach</p>
                          </div>
                        </Link>
                        <Link
                          to="/payment-history"
                          className="group relative rounded-xl border-2 border-secondary-100 bg-gradient-to-br from-secondary-50 to-white px-6 py-5 flex items-center space-x-3 hover:border-secondary-300 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="absolute inset-0" aria-hidden="true" />
                            <p className="text-sm font-semibold text-secondary-700 group-hover:text-secondary-800">My Pay</p>
                            <p className="text-sm text-gray-500 truncate">View payment history</p>
                          </div>
                        </Link>
                      </>
                    )}

                    {/* Client role quick actions */}
                    {userProfile && ['client_user', 'client_admin'].includes(userProfile.role) && (
                      <>
                        <Link
                          to="/leads"
                          className="group relative rounded-xl border-2 border-primary-100 bg-gradient-to-br from-primary-50 to-white px-6 py-5 flex items-center space-x-3 hover:border-primary-300 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="absolute inset-0" aria-hidden="true" />
                            <p className="text-sm font-semibold text-primary-700 group-hover:text-primary-800">View Leads</p>
                            <p className="text-sm text-gray-500 truncate">Manage your leads</p>
                          </div>
                        </Link>
                        <Link
                          to="/schedule"
                          className="group relative rounded-xl border-2 border-secondary-100 bg-gradient-to-br from-secondary-50 to-white px-6 py-5 flex items-center space-x-3 hover:border-secondary-300 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="absolute inset-0" aria-hidden="true" />
                            <p className="text-sm font-semibold text-secondary-700 group-hover:text-secondary-800">Schedule</p>
                            <p className="text-sm text-gray-500 truncate">Manage availability</p>
                          </div>
                        </Link>
                        <Link
                          to="/reports"
                          className="group relative rounded-xl border-2 border-coral-100 bg-gradient-to-br from-coral-50 to-white px-6 py-5 flex items-center space-x-3 hover:border-coral-300 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="absolute inset-0" aria-hidden="true" />
                            <p className="text-sm font-semibold text-coral-500 group-hover:text-coral-600">Reports</p>
                            <p className="text-sm text-gray-500 truncate">Analytics & metrics</p>
                          </div>
                        </Link>
                      </>
                    )}

                    {/* Provider role quick actions */}
                    {userProfile && ['provider_user', 'provider_admin'].includes(userProfile.role) && (
                      <>
                        <Link
                          to="/workloads/manage"
                          className="group relative rounded-xl border-2 border-primary-100 bg-gradient-to-br from-primary-50 to-white px-6 py-5 flex items-center space-x-3 hover:border-primary-300 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="absolute inset-0" aria-hidden="true" />
                            <p className="text-sm font-semibold text-primary-700 group-hover:text-primary-800">Manage Workloads</p>
                            <p className="text-sm text-gray-500 truncate">Assign & track workloads</p>
                          </div>
                        </Link>
                        <Link
                          to="/users"
                          className="group relative rounded-xl border-2 border-secondary-100 bg-gradient-to-br from-secondary-50 to-white px-6 py-5 flex items-center space-x-3 hover:border-secondary-300 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="absolute inset-0" aria-hidden="true" />
                            <p className="text-sm font-semibold text-secondary-700 group-hover:text-secondary-800">Manage Users</p>
                            <p className="text-sm text-gray-500 truncate">Add or edit users</p>
                          </div>
                        </Link>
                        <Link
                          to="/leads"
                          className="group relative rounded-xl border-2 border-accent-100 bg-gradient-to-br from-accent-50 to-white px-6 py-5 flex items-center space-x-3 hover:border-accent-300 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="absolute inset-0" aria-hidden="true" />
                            <p className="text-sm font-semibold text-accent-700 group-hover:text-accent-800">View Leads</p>
                            <p className="text-sm text-gray-500 truncate">Manage all leads</p>
                          </div>
                        </Link>
                        <Link
                          to="/financials"
                          className="group relative rounded-xl border-2 border-coral-100 bg-gradient-to-br from-coral-50 to-white px-6 py-5 flex items-center space-x-3 hover:border-coral-300 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="absolute inset-0" aria-hidden="true" />
                            <p className="text-sm font-semibold text-coral-500 group-hover:text-coral-600">Financials</p>
                            <p className="text-sm text-gray-500 truncate">Revenue & expenses</p>
                          </div>
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow-lg rounded-xl mb-8 border border-gray-100">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-primary-600" />
                      Upcoming Appointments
                    </h2>
                    <Link
                      to="/schedule"
                      className="text-sm text-primary-600 hover:text-primary-800 flex items-center"
                    >
                      View Schedule
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>

                  {loadingAppointments ? (
                    <div className="flex justify-center items-center h-24">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    </div>
                  ) : appointments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No upcoming appointments</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {appointments.map((appointment) => (
                        <div
                          key={appointment.id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center space-x-4">
                            <div className={`p-2 rounded-lg ${
                              appointment.eventType === 'tour'
                                ? 'bg-accent-100 text-accent-600'
                                : appointment.eventType === 'phone'
                                ? 'bg-secondary-100 text-secondary-600'
                                : 'bg-primary-100 text-primary-600'
                            }`}>
                              {appointment.eventType === 'tour' ? (
                                <User className="w-5 h-5" />
                              ) : (
                                <Clock className="w-5 h-5" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {appointment.leadName || 'Unknown Lead'}
                              </p>
                              <div className="flex items-center text-sm text-gray-500 space-x-2">
                                <span className="capitalize">{appointment.eventType}</span>
                                <span>•</span>
                                <span>{format(new Date(appointment.date), 'MMM d, yyyy')}</span>
                                <span>•</span>
                                <span>{formatTimeRange(appointment.startTime, appointment.endTime, projectTimezones[appointment.projectId])}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewAppointmentLead(appointment)}
                              className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              View Lead
                            </button>
                            <div className="relative">
                              <select
                                value={appointment.status}
                                onChange={(e) => handleAppointmentStatusUpdate(appointment.id, e.target.value as 'completed' | 'cancelled' | 'no-show')}
                                className="appearance-none px-3 py-1.5 pr-8 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                              >
                                <option value="scheduled">Scheduled</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="no-show">No Show</option>
                              </select>
                              <ChevronRight className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <h2 className="text-lg font-medium text-gray-900 mb-4">Overall Metrics</h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
                <MetricCard
                  title="Total Students"
                  value={loading ? '...' : overallMetrics.totalStudents}
                  icon={<Users className="w-6 h-6 text-white" />}
                  gradient="bg-gradient-to-br from-secondary-400 to-secondary-600"
                />
                <MetricCard
                  title="Enrolled Students"
                  value={loading ? '...' : overallMetrics.enrolledStudents}
                  icon={<CheckCircle className="w-6 h-6 text-white" />}
                  gradient="bg-gradient-to-br from-accent-400 to-accent-600"
                />
                <MetricCard
                  title="Conversion Rate"
                  value={loading ? '...' : `${overallMetrics.conversionRate}%`}
                  icon={<TrendingUp className="w-6 h-6 text-white" />}
                  gradient="bg-gradient-to-br from-primary-400 to-primary-600"
                />
                <MetricCard
                  title="Active Projects"
                  value={loading ? '...' : overallMetrics.activeProjects}
                  icon={<Target className="w-6 h-6 text-white" />}
                  gradient="bg-gradient-to-br from-coral-300 to-coral-500"
                />
              </div>

              <h2 className="text-lg font-medium text-gray-900 mb-4">Project Breakdown</h2>
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <LoadingSpinner />
                </div>
              ) : projectMetrics.length === 0 ? (
                <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
                  No active projects found
                </div>
              ) : (
                <div className="space-y-4">
                  {projectMetrics.map((project) => (
                    <div key={project.projectId} className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">{project.projectName}</h3>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Total Leads</p>
                          <p className="text-xl font-semibold">{project.totalLeads}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Students</p>
                          <p className="text-xl font-semibold">{project.totalStudents}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Valid Students</p>
                          <p className="text-xl font-semibold">{project.validStudents}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Enrolled</p>
                          <p className="text-xl font-semibold text-green-600">{project.enrolledStudents}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Conversion Rate</p>
                          <p className="text-xl font-semibold text-indigo-600">{project.conversionRate}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        </main>
      </div>

      {showLeadModal && selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => {
            setShowLeadModal(false);
            setSelectedLead(null);
          }}
          onLeadUpdated={() => {
            loadUpcomingAppointments();
          }}
        />
      )}
    </>
  );
};

export default DashboardPage;